import React, { useRef, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import TuneIcon from '@material-ui/icons/Tune';
import LanguageIcon from '@material-ui/icons/Language';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import PublicIcon from '@material-ui/icons/Public';
import LibraryBooksSharpIcon from '@material-ui/icons/LibraryBooksSharp';
import SearchIcon from '@material-ui/icons/Search';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import Slider from '@material-ui/core/Slider';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Divider from '@material-ui/core/Divider';
import { AppContext, DispatchContext } from '../AppContext';
import Image from 'material-ui-image'
import { makeStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WarningIcon from '@material-ui/icons/Warning';
import NotInterestedIcon from "@material-ui/icons/NotInterested";
import Service from "../services/service";
import * as actions from '../actions/actions';
import Skeleton from '@material-ui/lab/Skeleton';
import { config, getParaLevelString, resolveNotificationMessage, formatPercent } from '../helpers/constants';
import ReactTooltip from "react-tooltip";
import { useStaticState } from '@material-ui/pickers';
import Draggable from 'react-draggable';
import Paper from '@material-ui/core/Paper';
import CopyToClipboard from 'react-copy-to-clipboard';
import CanvasCompare from './CanvasCompare';
import IconLabel from './IconLabel';
import LoadingOverlay from './LoadingOverlay';
import Link from "@material-ui/core/Link";
import DividerLabel from './DividerLabel';


const variants = ['body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1', 'body1'];

const styles = (theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
        paddingRight: theme.spacing(18),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    pdfButton: {
        position: 'absolute',
        right: theme.spacing(6),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    settingsButton: {
        position: 'absolute',
        right: theme.spacing(11),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
    },
    dialog_container: {
        overflowY: 'hidden !important'
    },
    dialog: {
        height: '80vh',
    },
    container_text: {
        paddingTop: '0px',
        overflow: "hidden",
        flex: "1 auto",
    },
    content_body: {
        wordBreak: 'break-word',
        height: "100%",
        overflowY: "scroll",
        whiteSpace: (state) => { return (state.ui.showLinebreaks) ? "pre-line" : ""; }
    },
    text_panel: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
    },
    text_panel_header: {
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        padding: 4,
        marginBottom: 4
    },
    graph_container: {
        flex: "0 auto",
    },
    image_container: {
        borderBottom: '1px solid #085eec',
        marginLeft: "auto",
        marginRight: "auto",
        //width: "100%"
    },
    button_block: {
        minWidth: '120px',
    },
    button_icon: {
        paddingLeft: '5px',
    },
    image_style: {
        width: '100%',
        height: 'auto',
        position: 'inherit'
    }
}));

export function SimilarityCurve({ minRegionLength, minSimilarity, phraseAxisLabel, scale = 1 }) {
    const USE_BEZIER = true; // true = cubic hermite (S-curve); false = linear interpolation
    const PHRASE_LEN_AT_MIN_SIM = 150;
    const W = 248, H = 118;
    const ML = 34, MR = 8, MT = 10, MB = 30;
    const CW = W - ML - MR;
    const CH = H - MT - MB;
    const X_MIN = 0, X_MAX = 250;
    const Y_MIN = 20, Y_MAX = 100;
    const xs = (v) => ML + (v - X_MIN) / (X_MAX - X_MIN) * CW;
    const ys = (v) => MT + CH - (v - Y_MIN) / (Y_MAX - Y_MIN) * CH;
    // Linear: straight line from 100% at 0 chars to minSimilarity at PHRASE_LEN_AT_MIN_SIM chars
    const computeSimLinear = (pl) => {
        const y = 100 + (minSimilarity - 100) * pl / PHRASE_LEN_AT_MIN_SIM;
        return Math.max(minSimilarity, Math.min(100, y));
    };
    // Bezier: cubic hermite (smoothstep) with horizontal tangents at both anchors —
    // (0, 100%) and (PHRASE_LEN_AT_MIN_SIM, minSimilarity%) — producing an S-curve
    const computeSimBezier = (pl) => {
        const t = Math.min(1, Math.max(0, pl / PHRASE_LEN_AT_MIN_SIM));
        const y = 100 + (minSimilarity - 100) * (3 * t * t - 2 * t * t * t);
        return Math.max(minSimilarity, Math.min(100, y));
    };
    const computeSim = USE_BEZIER ? computeSimBezier : computeSimLinear;
    const pts = [];
    for (let x = X_MIN; x <= X_MAX; x += 2)
        pts.push(`${xs(x).toFixed(1)},${ys(computeSim(x)).toFixed(1)}`);
    // shaded accepted zone
    const startX = Math.min(Math.max(X_MIN, minRegionLength), X_MAX);
    const shadePts = [];
    for (let x = startX; x <= X_MAX; x += 2)
        shadePts.push(`${xs(x).toFixed(1)},${ys(computeSim(x)).toFixed(1)}`);
    const shadeArea = shadePts.length
        ? `${shadePts.join(' ')} ${xs(X_MAX).toFixed(1)},${(MT + CH).toFixed(1)} ${xs(startX).toFixed(1)},${(MT + CH).toFixed(1)}`
        : '';
    const xMRL       = xs(Math.min(minRegionLength, X_MAX));
    const xRef       = xs(PHRASE_LEN_AT_MIN_SIM);
    const yMs        = ys(minSimilarity);
    const yTop       = ys(100);
    const simAtMRL   = Math.round(computeSim(Math.min(Math.max(X_MIN, minRegionLength), X_MAX)));
    const yAtMRL     = ys(simAtMRL);
    const showMRLSim = Math.abs(yAtMRL - yMs) > 10 && Math.abs(yAtMRL - yTop) > 10;
    return (
        <svg width={W * scale} height={H * scale} style={{ display: 'block', overflow: 'visible', marginTop: 6, marginBottom: 2 }}>
            <g transform={`scale(${scale})`}>
            {/* ref grid lines */}
            <line x1={ML} y1={yMs}  x2={ML+CW} y2={yMs}  stroke="#e0e0e0" strokeWidth={1} strokeDasharray="3,3" />
            <line x1={ML} y1={yTop} x2={ML+CW} y2={yTop} stroke="#e0e0e0" strokeWidth={1} strokeDasharray="3,3" />
            {showMRLSim && <line x1={ML} y1={yAtMRL} x2={xMRL} y2={yAtMRL} stroke="#e0e0e0" strokeWidth={1} strokeDasharray="3,3" />}
            <line x1={xMRL} y1={MT} x2={xMRL} y2={MT+CH} stroke="#e0e0e0" strokeWidth={1} strokeDasharray="3,3" />
            <line x1={xRef} y1={MT} x2={xRef} y2={MT+CH} stroke="#e0e0e0" strokeWidth={1} strokeDasharray="3,3" />
            {/* accepted zone shading */}
            {shadeArea && <polygon points={shadeArea} fill="rgba(25,118,210,0.08)" />}
            {/* axes */}
            <line x1={ML} y1={MT}    x2={ML}    y2={MT+CH} stroke="#bbb" strokeWidth={1} />
            <line x1={ML} y1={MT+CH} x2={ML+CW} y2={MT+CH} stroke="#bbb" strokeWidth={1} />
            {/* curve */}
            <polyline points={pts.join(' ')} fill="none" stroke="#1976d2" strokeWidth={2} />
            {/* key point markers */}
            <circle cx={xMRL} cy={yAtMRL} r={3} fill="#1976d2" />
            <circle cx={xRef} cy={yMs} r={3} fill="#1976d2" />
            {/* x-axis labels */}
            <text x={xMRL} y={MT+CH+12} textAnchor="middle" fontSize={9} fill="#555">{minRegionLength}</text>
            {Math.abs(xRef - xMRL) > 20 && <text x={xRef} y={MT+CH+12} textAnchor="middle" fontSize={9} fill="#555">150</text>}
            {/* y-axis labels */}
            <text x={ML-3} y={yTop+4} textAnchor="end" fontSize={9} fill="#555">100%</text>
            {Math.abs(yMs - yTop) > 12 && <text x={ML-3} y={yMs+4} textAnchor="end" fontSize={9} fill="#555">{minSimilarity}%</text>}
            {showMRLSim && <text x={ML-3} y={yAtMRL+4} textAnchor="end" fontSize={9} fill="#1976d2">{simAtMRL}%</text>}
            {/* axis title */}
            <text x={ML+CW/2} y={MT+CH+26} textAnchor="middle" fontSize={9} fill="#aaa">{phraseAxisLabel || "phrase length (chars)"}</text>
            </g>
        </svg>
    );
}

function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

const DialogTitle = withStyles(styles)((props) => {
    const { children, classes, onClose, onExportPdf, exportPdfTooltip, onToggleSettings, settingsTooltip, settingsActive, ...other } = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography noWrap variant="h6" >{children}</Typography>
            {onToggleSettings ? (
                <Tooltip title={settingsTooltip || "Settings"}>
                    <IconButton aria-label="settings" className={classes.settingsButton} onClick={onToggleSettings} style={settingsActive ? { color: '#1976d2' } : {}}>
                        <TuneIcon />
                    </IconButton>
                </Tooltip>
            ) : null}
            {onExportPdf ? (
                <Tooltip title={exportPdfTooltip || "Export to PDF"}>
                    <IconButton aria-label="export to pdf" className={classes.pdfButton} onClick={onExportPdf}>
                        <CloudDownloadIcon />
                    </IconButton>
                </Tooltip>
            ) : null}
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        paddingTop: 0,
        flex: "1 1 auto",
        flexDirection: "column",
        display: "flex",
    },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(1),
    },
}))(MuiDialogActions);

let section1 = null;
let section2 = null;
export default function CompareSource(props) {
    const { open, onHandleClose, reloadReport, noRating } = props;
    const state = useContext(AppContext);
    const s = state.strings;
    const classes = useStyles(state);
    const dispatch = useContext(DispatchContext);
    const history = useHistory();
    const [hoveredPIdx, setHoveredPIdx] = useState(null);
    const [currentPhrasePos, setCurrentPhrasePos] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [showDummyText, setShowDummyText] = useState(false);
    const localSettings = state.compareSettings;
    const setLocalSettings = (updater) => {
        const next = typeof updater === 'function' ? updater(state.compareSettings) : updater;
        dispatch(actions.setCompareSettings(next));
    };
    const [sliderSettings, setSliderSettings] = React.useState(state.compareSettings);
    // Keep sliderSettings in sync when compareSettings changes from outside (e.g. on open)
    React.useEffect(() => { setSliderSettings(state.compareSettings); }, [state.compareSettings]);
    // Tracks the fingerprint state of the *stored* authsnap (not recomputed results).
    // Used to disable the fp toggles only when the underlying text is already a fingerprint.
    const originalFpRef = useRef({ text1: false, text2: false });
    const fpInitialized = useRef(false);

    // Reset phrase navigation when source/markup changes.
    useEffect(() => {
        setCurrentPhrasePos(null);
    }, [state.sourceMarkup]);

    // Reset when source changes so the next sourceMarkup load re-captures the original flags.
    useEffect(() => {
        fpInitialized.current = false;
    }, [state.activeSource?.Id]);

    useEffect(() => {
        if (state.sourceMarkup) {
            const fp1 = !!(state.sourceMarkup.Text1?.IsFingerprint);
            const fp2 = !!(state.sourceMarkup.Text2?.IsFingerprint);
            if (!fpInitialized.current) {
                originalFpRef.current = { text1: fp1, text2: fp2 };
                fpInitialized.current = true;
            }
            setLocalSettings(prev => ({
                ...prev,
                text1AsFingerprint: fp1,
                text2AsFingerprint: fp2,
            }));
        }
    }, [state.sourceMarkup]);

    const handleSettingsUpdate = () => {
        if (!state.activeSource?.Id) return;
        setSettingsLoading(true);
        const effectiveSettings = localSettings.legacyMatchMode
            ? { ...localSettings, minSimilarity: 0 }
            : localSettings;
        Service.getSourceMarkup(state.activeSource.Id, null, effectiveSettings).then(res => {
            setSettingsLoading(false);
            if (res.success && res.data) {
                dispatch(actions.setSourceMarkup(res.data));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors?.[0], s.notificationMessage), status: true }));
            }
        });
    };

    const getTextTypeIcon = (textObj) => {
        if (!textObj) return null;
        const type = textObj.Type;
        const libTextId = textObj.LibTextId;
        const isFingerprint = textObj.IsFingerprint;
        if (type === 'F' || isFingerprint) {
            return <Tooltip title="Fingerprint"><FingerprintIcon style={{ color: '#000066', fontSize: 22 }} /></Tooltip>;
        }
        if ((type === 'L') && libTextId) {
            return (
                <Tooltip title={textObj.Name}>
                    <IconButton size="small" style={{ padding: 0 }} onClick={() => window.open(`/libtext/${libTextId}`, '_blank', 'noopener,noreferrer')}>
                        <LibraryBooksSharpIcon style={{ color: '#000066', fontSize: 22 }} />
                    </IconButton>
                </Tooltip>
            );
        }
        if (type === 'W') {
            const url = textObj.Url;
            if (url) {
                return (
                    <Tooltip title={url}>
                        <IconButton size="small" style={{ padding: 0 }} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                            <PublicIcon style={{ color: '#000066', fontSize: 22 }} />
                        </IconButton>
                    </Tooltip>
                );
            }
            return <Tooltip title={textObj.Name || ""}><PublicIcon style={{ color: '#000066', fontSize: 22 }} /></Tooltip>;
        }
        return <Tooltip title={textObj.Name || ""}><SearchIcon style={{ color: '#000066', fontSize: 22 }} /></Tooltip>;
    };

    const handleClose = () => {
        onHandleClose(false)
    }

    const handleExportPdf = () => {
        if (state.activeSource && state.activeSource.Id) {
            dispatch(actions.openConfirmationDialog({
                title: s.compareSource.exportPdfTitle,
                text: s.compareSource.exportPdfText,
                hint: s.compareSource.exportPdfHint,
                waitingMessage: s.compareSource.exportPdfWaiting,
                onConfirm: () => exportSideBySidePdf(state.activeSource.Id)
            }));
        }
    }

    const exportSideBySidePdf = (sourceId) => {
        Service.createSideBySidePDF(sourceId, state.ui.permaLinkId).then(res => {
            dispatch(actions.closeConfirmationDialog());
            if (res.success) {
                if (res.data && res.data.url) {
                    var url = config.backendUrl + res.data.url.replace(/^\//, '');
                    var link = document.createElement("a");
                    link.style.display = 'none';
                    link.href = url;
                    link.click();
                    link.remove();
                }
                dispatch(actions.openToast({ type: "success", message: s.notificationMessage.pdfCreated || "PDF created successfully", status: true }));
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
            }
        });
    }

    const handleChangedRatingButton = (updateSoruceDetail) => {
        let data = []
        data.push({
            ...updateSoruceDetail,
            sid: state.activeSource.Id
        })

        Service.updateSources(data, state.activeReport.Id, state.ui.permaLinkId).then(res => {
            if (res.success) {
                dispatch(actions.openToast({ type: "success", message: s.notificationMessage.sourceUpdated, status: true }));
                reloadReport();
                onHandleClose(false);
            } else {
                dispatch(actions.openToast({ type: "error", message: resolveNotificationMessage(res.errors[0], s.notificationMessage), status: true }));
            }
        });
    };

    // Scoll page While hover source detail and source list
    const scrollToContent = (sourceParentId, sourceElementId, targetParentId, targetElementId) => {

        var targetElem = document.getElementById(targetElementId);
        var sourceElem = document.getElementById(sourceElementId);
        var targetWindow = document.getElementById(targetParentId);
        var sourceWindow = document.getElementById(sourceParentId);
        if (targetElem && sourceElem && targetWindow && sourceWindow) {

            var sourcePos = sourceElem.offsetTop - sourceWindow.scrollTop;
            targetWindow.scrollTo({ top: targetElem.offsetTop - sourcePos, behavior: 'smooth' });

            // attention: React will break down one <span> in multiple ones
            setTimeout(() => {
                var allTargetElems = document.querySelectorAll("[id=" + targetElementId + "]");
                allTargetElems.forEach(element => {
                    element.style.transition = 'opacity .4s ease-in';
                    element.style.opacity = '0.5';
                    setTimeout(() => {
                        if (element.style)
                            element.style.opacity = '1';
                    }, 500);
                });
            }, 0);
        }
    }

    // Build a map of PIdx -> index in state.sourceMarkup.Positions (for phrase navigation)
    const pIdxToPositionIndex = useMemo(() => {
        const positions = state.sourceMarkup?.Positions;
        if (!positions) return {};
        const map = {};
        positions.forEach((p, i) => { map[p.PIdx] = i; });
        return map;
    }, [state.sourceMarkup]);

    // Build a map of PIdx -> 1-based rank among matched phrases (sorted by position in text1)
    const phraseRankMap = useMemo(() => {
        const markup = state.sourceMarkup?.Text1?.Markup;
        if (!markup) return {};
        const map = {};
        let rank = 1;
        markup.forEach(item => { if (item.PIdx != null) map[item.PIdx] = rank++; });
        return map;
    }, [state.sourceMarkup]);

    const getPhraseTooltip = (source, simValue) => {
        const rank = phraseRankMap[source.PIdx];
        const total = state.sourceMarkup?.NumPhrases;
        const phraseText = source.Sub?.length ? source.Sub.map(s => s.T).join('') : (source.T || '');
        const charCount = phraseText.trim().length;
        const line2Parts = [];
        if (source.Words) line2Parts.push((s.compareSource?.phraseTooltipWords || "{0} Words").format(source.Words));
        if (charCount > 0) line2Parts.push((s.compareSource?.phraseTooltipChars || "{0} Chars").format(charCount));
        if (rank != null && total != null) line2Parts.push((s.compareSource?.phraseTooltipPhrase || "Phrase {0}/{1}").format(rank, total));
        const line1 = simValue ? getParaLevelString(simValue, s.paraphraseLevel) : null;
        const line2 = line2Parts.length ? line2Parts.join(' \u2013 ') : null;
        if (!line1 && !line2) return null;
        if (!line1) return line2;
        if (!line2) return line1;
        return `${line1}<br/>${line2}`;
    };

    // Handle hover from CanvasCompare
    const handleCanvasHover = useCallback((pIdx) => {
        setHoveredPIdx(pIdx);
    }, []);

    // Handle click from CanvasCompare - scroll both texts into view
    const handleCanvasClick = useCallback((pIdx) => {
        if (pIdx === null) return;

        const pi = pIdxToPositionIndex[pIdx];
        if (pi !== undefined) setCurrentPhrasePos(pi);

        const text1ElementId = 'Text1_' + pIdx;
        const text2ElementId = 'Text2_' + pIdx;

        const text1Element = document.getElementById(text1ElementId);
        const text1Container = document.getElementById('Text1');
        const text2Element = document.getElementById(text2ElementId);
        const text2Container = document.getElementById('Text2');

        if (text1Element && text1Container) {
            // Scroll so the phrase appears near the top of the container with a small margin.
            // Use getBoundingClientRect so the position is relative to the container's viewport,
            // not to offsetParent (which may be the document body).
            const SCROLL_MARGIN = 8;
            const containerRect = text1Container.getBoundingClientRect();
            const elementRect = text1Element.getBoundingClientRect();
            const relativeTop = elementRect.top - containerRect.top;
            const targetScrollTop = Math.max(0, text1Container.scrollTop + relativeTop - SCROLL_MARGIN);
            text1Container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });

            // Sync Text2 to match Text1's visual position of the phrase
            if (text2Element && text2Container) {
                const container2Rect = text2Container.getBoundingClientRect();
                const element2Rect = text2Element.getBoundingClientRect();
                const relativeTop2 = element2Rect.top - container2Rect.top;
                text2Container.scrollTo({ top: Math.max(0, text2Container.scrollTop + relativeTop2 - SCROLL_MARGIN), behavior: 'smooth' });
            }

            // Apply highlight animation to both sides
            [text1ElementId, text2ElementId].forEach(elementId => {
                var allElems = document.querySelectorAll("[id=" + elementId + "]");
                allElems.forEach(element => {
                    element.style.transition = 'opacity .4s ease-in';
                    element.style.opacity = '0.5';
                    setTimeout(() => {
                        if (element.style)
                            element.style.opacity = '1';
                    }, 500);
                });
            });
        }
    }, [pIdxToPositionIndex, setCurrentPhrasePos]);

    // Navigate to prev (-1) or next (+1) phrase
    const navigatePhrase = useCallback((direction) => {
        const positions = state.sourceMarkup?.Positions;
        if (!positions || positions.length === 0) return;
        const nextPos = currentPhrasePos === null
            ? (direction > 0 ? 0 : positions.length - 1)
            : Math.max(0, Math.min(positions.length - 1, currentPhrasePos + direction));
        setCurrentPhrasePos(nextPos);
        handleCanvasClick(positions[nextPos].PIdx);
    }, [state.sourceMarkup, currentPhrasePos, handleCanvasClick]);

    // Helper to get text span style based on hover state
    const getTextSpanStyle = (pIdx, baseColor) => {
        const isHovered = hoveredPIdx !== null && String(pIdx) === String(hoveredPIdx);

        // When hovered, make the hovered item lighter (more transparent), non-hovered stay solid
        return {
            backgroundColor: baseColor,
            marginLeft: 1,
            cursor: "pointer",
            opacity: isHovered ? 0.6 : 1,
            transition: 'opacity 0.15s ease-in-out',
            outline: isHovered ? '1px solid rgba(100,100,100,0.5)' : 'none',
            borderRadius: isHovered ? '2px' : '0'
        };
    };

    return (
        <div style={{ position: "relative" }}>
            <Dialog PaperComponent={PaperComponent} maxWidth={settingsOpen ? 'lg' : 'md'} classes={{ paper: classes.dialog }} fullWidth={true} disableBackdropClick={false} onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title" onClose={handleClose} onExportPdf={handleExportPdf} exportPdfTooltip={s.downloadReportDialog?.exportPdf || "Export to PDF"} onToggleSettings={state.user?.status === "Admin" ? () => setSettingsOpen(o => !o) : null} settingsTooltip={s.compareSource?.settingsTooltip || "Comparison settings"} settingsActive={settingsOpen}>
                    {state.activeSource.Title}
                </DialogTitle>

                <DialogContent dividers className={classes.dialog_container}>
                    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Grid container id="customized-dialog-header" className={classes.graph_container}>
                        <Grid alignContent="center" container className={classes.image_container}>
                            {!state.loadingSidebySideCompare ?
                                <Skeleton animation="wave" width={'100%'} height={118} /> :
                                <CanvasCompare
                                    width={920}
                                    height={120}
                                    style={{ width: '920px', height: '120px' }}
                                    onHover={handleCanvasHover}
                                    onClick={handleCanvasClick}
                                    hoveredPIdx={hoveredPIdx}
                                />
                            }
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} className={classes.container_text}>
                        <Grid item xs={6} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
                            <div className={classes.text_panel_header} style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div>
                                        {state.loadingSidebySideCompare && <Typography noWrap variant="h6">{state.sourceMarkup.Text1.Name}</Typography>}
                                        {!state.loadingSidebySideCompare && <Skeleton animation="wave" width="60%" />}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {!state.loadingSidebySideCompare
                                            ? <Skeleton animation="wave" width="40%" />
                                            : (state.sourceMarkup.Text1.TotalWords > 0)
                                                ? <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
                                                    {(state.sourceMarkup.Text1.IsFingerprint || state.sourceMarkup.Text1.Type === 'F') && <span style={{ fontStyle: 'italic' }}>{s.compareSource?.approxPrefix || "Approx."}{' '}</span>}
                                                    {(s.compareSource?.matchingWords || "{3}% – {0}/{1} words in {2} phrases").format(state.sourceMarkup.Text1.MatchWords, state.sourceMarkup.Text1.TotalWords, state.sourceMarkup.NumPhrases ?? 0, formatPercent(state.sourceMarkup.Text1.Percent))}
                                                  </Typography>
                                                : <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic', flex: 1 }}>{s.compareSource?.noStatsForFingerprint || "No text statistics available for text signatures"}</Typography>
                                        }
                                        {state.sourceMarkup?.Positions?.length > 0 && (
                                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                                <Tooltip title={s.compareSource?.prevPhrase || "Previous phrase"}>
                                                    <span>
                                                        <IconButton size="small" disabled={currentPhrasePos === null || currentPhrasePos === 0} onClick={() => navigatePhrase(-1)}>
                                                            <SkipPreviousIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Typography variant="caption" style={{ minWidth: 36, textAlign: 'center', lineHeight: 1, flexShrink: 0 }}>
                                                    {currentPhrasePos !== null
                                                        ? `${currentPhrasePos + 1}/${state.sourceMarkup.Positions.length}`
                                                        : `–/${state.sourceMarkup.Positions.length}`}
                                                </Typography>
                                                <Tooltip title={s.compareSource?.nextPhrase || "Next phrase"}>
                                                    <span>
                                                        <IconButton size="small" disabled={currentPhrasePos === state.sourceMarkup.Positions.length - 1} onClick={() => navigatePhrase(1)}>
                                                            <SkipNextIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {state.sourceMarkup?.Text1 && (
                                    <div style={{ flexShrink: 0, marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                                        {getTextTypeIcon(state.sourceMarkup.Text1)}
                                    </div>
                                )}
                            </div>
                            <div id="Text1" className={classes.content_body}>
                            {!state.loadingSidebySideCompare && variants.map((variant, i) => (
                                <Typography key={i} variant={variant}>
                                    {<Skeleton animation="wave" width={'100%'} />}
                                </Typography>
                            ))}
                            {state.sourceMarkup && state.sourceMarkup.Text1 && state.sourceMarkup.Text1.IsFingerprint && !showDummyText &&
                                <IconLabel type="info" label={s.libraryText.isFingerprint} />
                            }
                            {state.sourceMarkup && state.sourceMarkup.Text1 && (!state.sourceMarkup.Text1.IsFingerprint || showDummyText) &&
                                state.sourceMarkup.Text1.Markup.map((source, index) => {
                                    const isDummyText = showDummyText && state.sourceMarkup.Text1.IsFingerprint;
                                    const renderT = (t) => isDummyText ? String(t).replace(/\ba+\b/g, (m) => '?'.repeat(m.length).trim()) : t;
                                    if (source.PIdx == null) {
                                        if (source.ERIdx != null)
                                            return <span key={index} style={{ color: '#bbbbbb' }}>{renderT(source.T)}</span>;
                                        return renderT(source.T);
                                    }
                                    var color = state.ui.colors[source.PIdx % 10];
                                    return (
                                        <span id={'Text1_' + source.PIdx} onClick={() => { scrollToContent('Text1', 'Text1_' + source.PIdx, 'Text2', 'Text2_' + source.PIdx); const pi = pIdxToPositionIndex[source.PIdx]; if (pi !== undefined) setCurrentPhrasePos(pi); }} onMouseEnter={() => setHoveredPIdx(source.PIdx)} onMouseLeave={() => setHoveredPIdx(null)} data-for="compareTT" data-tip={getPhraseTooltip(source, source.Sim1 || source.Sim)} ref={section2} key={index} style={getTextSpanStyle(source.PIdx, color)}>
                                            {(source.Sub && source.Sub.length > 0) ? source.Sub.map((sub, si) => (
                                                <span key={si} style={{ fontWeight: (sub.MIdx != null) ? 'bold' : 'normal' }}>{renderT(sub.T)}</span>
                                            )) : renderT(source.T)}
                                        </span>
                                    );
                                })}
                            </div>
                        </Grid>
                        <Grid item xs={6} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', height: '100%' }}>
                            <div className={classes.text_panel_header} style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div>
                                        {state.loadingSidebySideCompare && <Typography noWrap variant="h6">{state.sourceMarkup.Text2.Name}</Typography>}
                                        {!state.loadingSidebySideCompare && <Skeleton animation="wave" width="60%" />}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        {!state.loadingSidebySideCompare
                                            ? <Skeleton animation="wave" width="40%" />
                                            : (state.sourceMarkup.Text2.TotalWords > 0)
                                                ? <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
                                                    {(state.sourceMarkup.Text2.IsFingerprint || state.sourceMarkup.Text2.Type === 'F') && <span style={{ fontStyle: 'italic' }}>{s.compareSource?.approxPrefix || "Approx."}{' '}</span>}
                                                    {(s.compareSource?.matchingWords || "{3}% – {0}/{1} words in {2} phrases").format(state.sourceMarkup.Text2.MatchWords, state.sourceMarkup.Text2.TotalWords, state.sourceMarkup.NumPhrases ?? 0, formatPercent(state.sourceMarkup.Text2.Percent))}
                                                  </Typography>
                                                : <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic', flex: 1 }}>{s.compareSource?.noStatsForFingerprint || "No text statistics available for text signatures"}</Typography>
                                        }
                                        {state.sourceMarkup?.Positions?.length > 0 && (
                                            <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                                <Tooltip title={s.compareSource?.prevPhrase || "Previous phrase"}>
                                                    <span>
                                                        <IconButton size="small" disabled={currentPhrasePos === null || currentPhrasePos === 0} onClick={() => navigatePhrase(-1)}>
                                                            <SkipPreviousIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Typography variant="caption" style={{ minWidth: 36, textAlign: 'center', lineHeight: 1, flexShrink: 0 }}>
                                                    {currentPhrasePos !== null
                                                        ? `${currentPhrasePos + 1}/${state.sourceMarkup.Positions.length}`
                                                        : `–/${state.sourceMarkup.Positions.length}`}
                                                </Typography>
                                                <Tooltip title={s.compareSource?.nextPhrase || "Next phrase"}>
                                                    <span>
                                                        <IconButton size="small" disabled={currentPhrasePos === state.sourceMarkup.Positions.length - 1} onClick={() => navigatePhrase(1)}>
                                                            <SkipNextIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {state.sourceMarkup?.Text2 && (
                                    <div style={{ flexShrink: 0, marginLeft: 8, display: 'flex', alignItems: 'center' }}>
                                        {getTextTypeIcon(state.sourceMarkup.Text2)}
                                    </div>
                                )}
                            </div>
                            <div id="Text2" className={classes.content_body}>
                            {variants.map((variant, i) => (
                                <Typography key={i} variant={variant}>
                                    {!state.loadingSidebySideCompare && <Skeleton animation="wave" width={'100%'} />}
                                </Typography>
                            ))}
                            {state.sourceMarkup && state.sourceMarkup.Text2 && state.sourceMarkup.Text2.IsFingerprint && !showDummyText &&
                                <IconLabel type="info" label={s.libraryText.isFingerprint} />
                            }

                            {state.sourceMarkup && state.sourceMarkup.Text2 && (!state.sourceMarkup.Text2.IsFingerprint || showDummyText) &&
                                state.sourceMarkup.Text2.Markup.map((source, index) => {
                                    const isDummyText = showDummyText && state.sourceMarkup.Text2.IsFingerprint;
                                    const renderT = (t) => isDummyText ? String(t).replace(/\ba+\b/g, (m) => '?'.repeat(m.length).trim()) : t;
                                    if (source.PIdx == null) return renderT(source.T);
                                    var color = state.ui.colors[source.PIdx % 10];
                                    return (
                                        <span id={'Text2_' + source.PIdx} onClick={() => { scrollToContent('Text2', 'Text2_' + source.PIdx, 'Text1', 'Text1_' + source.PIdx); const pi = pIdxToPositionIndex[source.PIdx]; if (pi !== undefined) setCurrentPhrasePos(pi); }} onMouseEnter={() => setHoveredPIdx(source.PIdx)} onMouseLeave={() => setHoveredPIdx(null)} data-for="compareTT" data-tip={getPhraseTooltip(source, source.Sim2 || source.Sim)} ref={section2} key={index} style={getTextSpanStyle(source.PIdx, color)}>
                                            {(source.Sub && source.Sub.length > 0) ? source.Sub.map((sub, si) => (
                                                <span key={si} style={{ fontWeight: (sub.MIdx != null) ? 'bold' : 'normal' }}>{renderT(sub.T)}</span>
                                            )) : renderT(source.T)}
                                        </span>
                                    );
                                })}
                            </div>
                        </Grid>
                    </Grid>
                        </div>{/* end main content */}
                        {settingsOpen && (
                            <div style={{ position: 'relative', width: 320, flexShrink: 0, borderLeft: '1px solid #e0e0e0', paddingLeft: 16, paddingRight: 16, marginLeft: 8, overflowY: 'auto' }}>
                                <LoadingOverlay visible={settingsLoading} />
                                <DividerLabel label={s.compareSource?.settingsTitle || "Comparison Settings"} tooltip={s.compareSource?.settingsHint} />
                                <div style={{ marginBottom: 12, marginTop: 8 }}>
                                    <Typography variant="caption" color="textSecondary">{s.compareSource?.minRegionLength || "Minimum phrase length (chars)"}</Typography>
                                    <Slider
                                        value={sliderSettings.minRegionLength}
                                        min={10} max={250} step={10}
                                        valueLabelDisplay="auto"
                                        onChange={(e, v) => setSliderSettings(prev => ({ ...prev, minRegionLength: v }))}
                                        onChangeCommitted={(e, v) => setLocalSettings(prev => ({ ...prev, minRegionLength: v }))}
                                    />
                                </div>
                                <div style={{ marginBottom: 4 }}>
                                    <Typography variant="caption" color="textSecondary">{s.compareSource?.minSimilarity || "Minimum similarity (%)"}</Typography>
                                    <Slider
                                        value={sliderSettings.minSimilarity}
                                        min={25} max={100} step={5}
                                        valueLabelDisplay="auto"
                                        disabled={!!sliderSettings.legacyMatchMode}
                                        onChange={(e, v) => setSliderSettings(prev => ({ ...prev, minSimilarity: v }))}
                                        onChangeCommitted={(e, v) => setLocalSettings(prev => ({ ...prev, minSimilarity: v }))}
                                    />
                                </div>
                                {sliderSettings.legacyMatchMode
                                    ? <IconLabel type="info" mt={8} label={s.compareSource?.legacyNoSimilarityDiagram || "Similarity settings are not supported in legacy match mode"} />
                                    : <SimilarityCurve minRegionLength={sliderSettings.minRegionLength} minSimilarity={sliderSettings.minSimilarity} phraseAxisLabel={s.compareSource?.phraseAxisLabel} />
                                }
                                <FormControlLabel
                                    label={s.compareSource?.legacyMatchMode || "Use legacy match mode"}
                                    control={
                                        <Switch
                                            checked={!!localSettings.legacyMatchMode}
                                            onChange={(e) => {
                                                const on = e.target.checked;
                                                setLocalSettings(prev => ({ ...prev, legacyMatchMode: on }));
                                                setSliderSettings(prev => ({ ...prev, legacyMatchMode: on }));
                                            }}
                                            color="primary"
                                        />
                                    }
                                />
                                <Divider style={{ margin: '8px 0' }} />
                                {state.user?.status === "Admin" && (<>
                                <FormControlLabel
                                    label={s.compareSource?.text1AsFingerprint || "Compare text 1 as fingerprint"}
                                    control={
                                        <Switch
                                            checked={localSettings.text1AsFingerprint}
                                            disabled={originalFpRef.current.text1}
                                            onChange={(e) => setLocalSettings(prev => ({ ...prev, text1AsFingerprint: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                />
                                <FormControlLabel
                                    label={s.compareSource?.text2AsFingerprint || "Compare text 2 as fingerprint"}
                                    control={
                                        <Switch
                                            checked={localSettings.text2AsFingerprint}
                                            disabled={originalFpRef.current.text2}
                                            onChange={(e) => setLocalSettings(prev => ({ ...prev, text2AsFingerprint: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                />
                                <Divider style={{ margin: '8px 0' }} />
                                </>)}
                                <FormControlLabel
                                    label={s.compareSource?.showLinebreaks || "Show line breaks"}
                                    control={
                                        <Switch
                                            checked={state.ui.showLinebreaks}
                                            onChange={(e) => dispatch(actions.setUI({ showLinebreaks: e.target.checked }))}
                                            color="primary"
                                        />
                                    }
                                />
                                <FormControlLabel
                                    label={s.compareSource?.showDummyText || "Show dummy text for text signatures"}
                                    control={
                                        <Switch
                                            checked={showDummyText}
                                            onChange={(e) => setShowDummyText(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                />
                                <div style={{ marginTop: 16 }}>
                                    <Button fullWidth variant="contained" color="primary" onClick={handleSettingsUpdate} disabled={settingsLoading}>
                                        {s.compareSource?.updateButton || "Update"}
                                    </Button>
                                </div>
                                {state.user?.status === "Admin" && state.activeSource?.Id && (
                                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 16, wordBreak: 'break-all' }}>
                                        {s.compareSource?.snapshotId || "Snapshot ID"}: {state.activeSource.Id}
                                    </Typography>
                                )}
                            </div>
                        )}
                    </div>{/* end flex row */}
                </DialogContent>

                <DialogActions>
                    {!noRating &&
                        <>
                            <Button className={classes.button_block} disabled={state.activeReport.ReadOnly || (state.activeSource.Accepted && state.activeSource.Used)} onClick={() => handleChangedRatingButton(
                                { acc: 1 }
                            )} variant="contained" color="primary">
                                <CheckCircleIcon /> <span className={classes.button_icon}>{s.accept}</span>
                            </Button>
                            <Button className={classes.button_block} disabled={state.activeReport.ReadOnly || (state.activeSource.Excluded)} onClick={() => handleChangedRatingButton(
                                { exc: 1 }
                            )} variant="contained" color="primary">
                                <NotInterestedIcon /> <span className={classes.button_icon}> {s.exclude} </span>
                            </Button>
                            <Button className={classes.button_block} disabled={state.activeReport.ReadOnly || (!state.activeSource.Accepted && state.activeSource.Used)} onClick={() => handleChangedRatingButton(
                                { acc: 0 }
                            )} variant="contained" color="primary">
                                <WarningIcon /> <span className={classes.button_icon}>{s.suspicious}</span>
                            </Button>
                        </>
                    }
                    <Button className={classes.button_block} onClick={handleClose} variant="outlined" color="primary">
                        {s.cancel}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}